import { Stack } from "@mui/material"
import { Context, ReactElement, useContext } from "react"

import { POSITIVE_DECIMAL_REGEX } from "../../../../../constants/patterns"
import {
    TT_DEVICE_PHASES,
    TT_EVDC_HARMONIC_TEST_STANDARDS,
    TT_EVDC_MAX_DEMAND_CURRENT,
    TT_EVDC_MAX_DEMAND_POWER,
    TT_EVDC_MODEL_REF,
    TT_EVDC_MODULE,
    TT_EVDC_MODULE_INPUT_RATED_CURRENT,
    TT_EVDC_MODULE_INPUT_RATED_POWER,
    TT_EVDC_TOT_INPUT_RATED_CURRENT,
    TT_EVDC_TOT_INPUT_RATED_POWER,
    TT_EVDC_TYPE,
    TT_MODEL,
    TT_VOLTAGE,
} from "../../../../../constants/tooltips"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormErrors from "../../../../formErrors/formErrors"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import ValidatedRadioGroup from "../../../../inputs/validated/validatedRadioGroup"
import ValidatedAutocomplete from "../../../../inputs/validated/validatedSelect"
import ValidatedTextField from "../../../../inputs/validated/validatedTextField"
import CoreDeviceDetailsSkeleton from "../../../../loading/skeletons/coreDeviceDetailsSkeleton"
import { useFormGenerator } from "../../../../submitApplication/single/formGenerator/useFormGenerator"
import { AppCard } from "../../../../surfaces/appCard"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import {
    EVDC_COMMENTS_APP_TEST_STANDARDS_NOTES_REG,
    EVDC_HARMONIC_TEST_STANDARDS,
    EVDC_HARMONIC_TEST_STANDARDS_REG_REG,
    EVDC_MAX_DEMAND_CURRENT_REG,
    EVDC_MAX_DEMAND_POWER_REG,
    EVDC_MODEL_REF_REG,
    EVDC_MODEL_REG,
    EVDC_MODULES,
    EVDC_MODULE_INPUT_RATED_CURRENT_REG,
    EVDC_MODULE_INPUT_RATED_POWER_REG,
    EVDC_MODULE_REG,
    EVDC_PHASES_REG,
    EVDC_TOT_INPUT_RATED_CURRENT_REG,
    EVDC_TOT_INPUT_RATED_POWER_REG,
    EVDC_TYPES,
    EVDC_TYPE_REG,
    EVDC_VOLTAGE_OPTIONS,
    EVDC_VOLTAGE_REG,
    LCT_PHASES_OPTIONS,
    evdcDeviceFormLabelMapping,
    getEvdcUpdatedFormData,
    mapEvdcRegisterNameToValues,
} from "../../formSpecs/evdcSpec"
import { EVDCDeviceFormData } from "../../types/providerTypes"

interface ICoreDeviceDetailsProps {
    context: Context<IDeviceFormContextValue<EVDCDeviceFormData>>
}

/** Displays a form page that allows the user to enter the core
 * information about an EVDC device
 */
const CoreEvdcDeviceDetailsForm = ({
    context,
}: ICoreDeviceDetailsProps): ReactElement => {
    const {
        currentIndex,
        setCurrentIndex,
        formData,
        saveDraft,
        saveDraftLoading,
        setFormData,
        errors: contextErrors,
        formIsLoading,
    } = useContext(context)

    const isFirstPage = currentIndex === 0

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useFormGenerator(mapEvdcRegisterNameToValues(formData))

    const updateCoreDeviceDetails = (): void => {
        const updatedFormData = getEvdcUpdatedFormData(formData, watch)
        setFormData(updatedFormData)
    }

    const onClickBack = isFirstPage
        ? undefined
        : (): void => {
              updateCoreDeviceDetails()
              setCurrentIndex(currentIndex - 1)
          }

    const onClickNext = (): void => {
        updateCoreDeviceDetails()
        setCurrentIndex(currentIndex + 1)
    }

    const onSaveDraft = (): void => {
        const updatedFormData = getEvdcUpdatedFormData(formData, watch)
        setFormData(updatedFormData)
        saveDraft(updatedFormData)
    }

    if (contextErrors.length > 0) {
        return <ErrorDisplay error={contextErrors[0]} />
    }

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <>
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVDC_MODEL_REG}
                        tooltip={TT_MODEL}
                        label={evdcDeviceFormLabelMapping[EVDC_MODEL_REG]}
                        placeholder="Enter device model..."
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVDC_MODEL_REF_REG}
                        tooltip={TT_EVDC_MODEL_REF}
                        label={evdcDeviceFormLabelMapping[EVDC_MODEL_REF_REG]}
                        placeholder="Enter device model reference..."
                    />
                    <ValidatedAutocomplete
                        control={control}
                        required
                        registerName={EVDC_TYPE_REG}
                        tooltip={TT_EVDC_TYPE}
                        label={evdcDeviceFormLabelMapping[EVDC_TYPE_REG]}
                        options={EVDC_TYPES}
                    />
                    <ValidatedAutocomplete
                        control={control}
                        required
                        registerName={EVDC_MODULE_REG}
                        tooltip={TT_EVDC_MODULE}
                        label={evdcDeviceFormLabelMapping[EVDC_MODULE_REG]}
                        options={EVDC_MODULES}
                    />
                    <ValidatedRadioGroup
                        control={control}
                        required
                        registerName={EVDC_PHASES_REG}
                        tooltip={TT_DEVICE_PHASES}
                        label={evdcDeviceFormLabelMapping[EVDC_PHASES_REG]}
                        radioOptions={LCT_PHASES_OPTIONS}
                    />
                    <ValidatedAutocomplete
                        control={control}
                        required
                        registerName={EVDC_VOLTAGE_REG}
                        tooltip={TT_VOLTAGE}
                        label={evdcDeviceFormLabelMapping[EVDC_VOLTAGE_REG]}
                        options={EVDC_VOLTAGE_OPTIONS}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVDC_TOT_INPUT_RATED_CURRENT_REG}
                        tooltip={TT_EVDC_TOT_INPUT_RATED_CURRENT}
                        label={
                            evdcDeviceFormLabelMapping[
                                EVDC_TOT_INPUT_RATED_CURRENT_REG
                            ]
                        }
                        placeholder="Enter total input rated current..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Total input rated current must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVDC_TOT_INPUT_RATED_POWER_REG}
                        tooltip={TT_EVDC_TOT_INPUT_RATED_POWER}
                        label={
                            evdcDeviceFormLabelMapping[
                                EVDC_TOT_INPUT_RATED_POWER_REG
                            ]
                        }
                        placeholder="Enter total input rated power..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Total input rated power must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVDC_MAX_DEMAND_CURRENT_REG}
                        tooltip={TT_EVDC_MAX_DEMAND_CURRENT}
                        label={
                            evdcDeviceFormLabelMapping[
                                EVDC_MAX_DEMAND_CURRENT_REG
                            ]
                        }
                        placeholder="Enter device max current demand..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Device max current demand must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVDC_MAX_DEMAND_POWER_REG}
                        tooltip={TT_EVDC_MAX_DEMAND_POWER}
                        label={
                            evdcDeviceFormLabelMapping[
                                EVDC_MAX_DEMAND_POWER_REG
                            ]
                        }
                        placeholder="Enter device max demand power..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Device max demand power must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVDC_MODULE_INPUT_RATED_CURRENT_REG}
                        label={
                            evdcDeviceFormLabelMapping[
                                EVDC_MODULE_INPUT_RATED_CURRENT_REG
                            ]
                        }
                        tooltip={TT_EVDC_MODULE_INPUT_RATED_CURRENT}
                        placeholder="Enter module input rated current..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Module input rated current must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={EVDC_MODULE_INPUT_RATED_POWER_REG}
                        label={
                            evdcDeviceFormLabelMapping[
                                EVDC_MODULE_INPUT_RATED_POWER_REG
                            ]
                        }
                        tooltip={TT_EVDC_MODULE_INPUT_RATED_POWER}
                        placeholder="Enter module input rated power..."
                        customRules={{
                            pattern: {
                                value: POSITIVE_DECIMAL_REGEX,
                                message:
                                    "Module input rated power must be a valid positive number",
                            },
                        }}
                    />
                    <ValidatedAutocomplete
                        control={control}
                        required
                        registerName={EVDC_HARMONIC_TEST_STANDARDS_REG_REG}
                        tooltip={TT_EVDC_HARMONIC_TEST_STANDARDS}
                        label={
                            evdcDeviceFormLabelMapping[
                                EVDC_HARMONIC_TEST_STANDARDS_REG_REG
                            ]
                        }
                        options={EVDC_HARMONIC_TEST_STANDARDS}
                    />
                    <ValidatedTextField
                        control={control}
                        required
                        registerName={
                            EVDC_COMMENTS_APP_TEST_STANDARDS_NOTES_REG
                        }
                        label={
                            evdcDeviceFormLabelMapping[
                                EVDC_COMMENTS_APP_TEST_STANDARDS_NOTES_REG
                            ]
                        }
                        placeholder="Enter any comments on testing standards..."
                    />
                    <FormErrors
                        errors={errors}
                        getFriendlyName={(key: string) =>
                            evdcDeviceFormLabelMapping[key]
                        }
                    />
                    <FormNavigation
                        onClickBack={onClickBack}
                        onClickNext={() => handleSubmit(onClickNext)()}
                        nextText="Next"
                        allDisabled={formIsLoading}
                        onClickMiddle={onSaveDraft}
                        middleDisabled={saveDraftLoading}
                    />
                </>
            </Stack>
        </AppCard>
    )
}

const CoreEvdcDeviceDetails = ({
    context,
}: ICoreDeviceDetailsProps): ReactElement => {
    const { formIsLoading, currentIndex } = useContext(context)

    return (
        <AppCard>
            <Stack spacing={GRID_SPACING * 2}>
                <FormHeader
                    header="Core EV Charge Point (DC) Device Details"
                    subHeader="To start, please enter the following core details about the device"
                    index={currentIndex + 1}
                />
                {formIsLoading ? (
                    <CoreDeviceDetailsSkeleton />
                ) : (
                    <CoreEvdcDeviceDetailsForm context={context} />
                )}
            </Stack>
        </AppCard>
    )
}

export default CoreEvdcDeviceDetails
